import { Activity, AlertTriangle, CheckCircle2, ChevronDown, Cpu, Loader, RotateCcw, Server, XCircle } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { useChannels } from '@/api/endpoints/channels'
import { thumbnailUrl } from '@/api/endpoints/media'
import {
  ACTIVE_JOB_STATUSES,
  useJobsFeed,
  useRetryJob,
  type Job,
  type JobStatus,
} from '@/api/endpoints/jobs'
import { Chip, type ChipVariant } from '@/components/ui/Chip'
import { EmptyState } from '@/components/ds/EmptyState'
import { PageHeader } from '@/components/ds/PageHeader'
import { Section } from '@/components/ds/Section'
import { CancelJobButton } from '@/features/jobs/components/CancelJobButton'
import { failureReason } from '@/features/jobs/failureReason'
import { canRetryJob, jobKindLabel } from '@/features/jobs/jobKind'
import {
  extractJobVideos,
  isRowToggleClick,
  jobChannelLabel,
  publishLabel,
  type JobVideo,
} from '@/features/jobs/jobVideos'
import { hasLocalLlm, llmHostLabel, llmHostsSummary } from '@/features/jobs/llm'
import { activeSummary, queueLabel } from '@/features/jobs/queue'
import { PlatformChip } from '@/features/platforms/PlatformChip'
import { cn } from '@/lib/cn'
import { errorMessage } from '@/lib/errors'
import { formatDurationHuman } from '@/lib/format'
import { preservingChannel } from '@/lib/routing'
import styles from './JobsPage.module.scss'
import { QueryErrorNotice } from '@/components/feedback/QueryErrorNotice'

/**
 * Global list of the user's jobs. Split into "Ativos" (still running) and
 * "Recentes" (terminal). Reads the same shared feed that drives the sidebar
 * badge, so the list is live without any extra polling.
 */
export function JobsPage() {
  const { data, isPending, isError, error, refetch } = useJobsFeed()
  // Build slug → platform once so each job row can flag whether it's a
  // YouTube or TikTok upload without N extra requests.
  const { data: channels = [] } = useChannels()
  const platformBySlug = useMemo(
    () => new Map(channels.map((c) => [c.slug, c.platform] as const)),
    [channels],
  )

  if (isPending) {
    return (
      <div className={styles.page}>
        <Header />
        <p className={styles.meta}>Carregando jobs...</p>
      </div>
    )
  }
  if (isError) {
    return (
      <div className={styles.page}>
        <Header />
        <Section>
          <QueryErrorNotice
            error={error}
            label="os jobs"
            onRetry={() => {
              void refetch()
            }}
          />
        </Section>
      </div>
    )
  }

  const jobs = data.data
  const active = jobs.filter((j) => ACTIVE_JOB_STATUSES.includes(j.status))
  const recent = jobs.filter((j) => !ACTIVE_JOB_STATUSES.includes(j.status))

  if (jobs.length === 0) {
    return (
      <div className={styles.page}>
        <Header />
        <Section>
          <EmptyState
            icon={<Activity />}
            title="Nenhum job ainda"
            description="Quando você iniciar uma geração, upload ou ferramenta, ela aparece aqui."
          />
        </Section>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <Header />
      <div className={styles.sections}>
        {active.length > 0 ? (
          <Section
            title="Ativos"
            description={activeSummary(active)}
          >
            <div className={styles.list}>
              {active.map((job) => (
                <JobRow
                  key={job.id}
                  job={job}
                  platform={(job.channel_slug && platformBySlug.get(job.channel_slug)) || null}
                />
              ))}
            </div>
          </Section>
        ) : null}

        {recent.length > 0 ? (
          <Section title="Recentes" description={`${String(recent.length)} concluídos`}>
            <div className={styles.list}>
              {recent.map((job) => (
                <JobRow
                  key={job.id}
                  job={job}
                  platform={(job.channel_slug && platformBySlug.get(job.channel_slug)) || null}
                />
              ))}
            </div>
          </Section>
        ) : null}
      </div>
    </div>
  )
}

function Header() {
  return (
    <PageHeader
      eyebrow="Atividade"
      title="Jobs"
      description="Gerações, uploads e ferramentas em execução ou recém-concluídos."
    />
  )
}

interface JobRowProps {
  job: Job
  platform: 'youtube' | 'tiktok' | null
}

function JobRow({ job, platform }: JobRowProps) {
  const videos = extractJobVideos(job)
  // O "Abrir" leva ao primeiro vídeo que ainda existe (com idioma, dá link).
  const firstVideo = videos.find((v) => v.language !== null) ?? null
  const channelLabel = jobChannelLabel(job)
  const studioNow = extractStudioCurrent(job)
  const engagementNow = extractEngagementCurrent(job)
  const engagement = extractEngagementItems(job)
  const progress = typeof job.progress === 'number' ? job.progress : 0
  const isActive = ACTIVE_JOB_STATUSES.includes(job.status)
  const duration = typeof job.duration_seconds === 'number' ? job.duration_seconds : null
  const hosts = job.llm_hosts ?? null
  // The row expands into a per-video + LLM-host breakdown (card #76). Only
  // offer the disclosure when there's something to show underneath.
  const warnings = extractWarnings(job)
  const hasDetail =
    videos.length > 0 ||
    engagement.length > 0 ||
    hasLocalLlm(hosts) ||
    duration != null ||
    warnings.length > 0
  const [expanded, setExpanded] = useState(false)
  const retry = useRetryJob()
  // "Continuar" só faz sentido em jobs falhos que a retomada sabe tratar:
  // upload (reenvio com guarda anti-duplicata) e geração viral_shorts (piloto
  // do checkpoint no PR1). Os demais tipos dariam 422 (F4b: a retomada
  // despacha pelo tipo, não pela categoria).
  const canRetry = canRetryJob(job)
  const onRetry = () => {
    retry.mutate(
      { jobId: job.id },
      {
        onSuccess: () => { toast.success('Retomada iniciada — acompanhe o novo job na lista.') },
        onError: (err) => { toast.error(errorMessage(err, 'Não foi possível retomar o job.')) },
      },
    )
  }
  // The failure reason lives behind a disclosure so a wall of red errors
  // doesn't drown the feed — only failed/cancelled jobs surface a toggle.
  const reason = failureReason(job)
  const [showReason, setShowReason] = useState(false)
  const reasonId = `job-reason-${job.id}`
  const detailId = `job-detail-${job.id}`

  return (
    <div className={styles.rowWrap}>
      {/* O clique na linha abre e fecha o detalhe (o chevron continua sendo o
          controle acessível por teclado); links e botões dentro dela não. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className={cn(styles.row, hasDetail && styles.rowClickable)}
        onClick={(e) => {
          // O diálogo do Cancelar é um portal: o clique nele sobe pela árvore
          // do React até aqui, mas não está dentro da linha no DOM.
          if (hasDetail && e.currentTarget.contains(e.target as Node) && isRowToggleClick(e.target)) {
            setExpanded((v) => !v)
          }
        }}
      >
        <div className={styles.statusCell}>
          {statusIcon(job.status)}
          <Chip variant={chipVariant(job.status)}>{queueLabel(job)}</Chip>
        </div>

        <div className={styles.body}>
          <div className={styles.headline}>
            <span className={styles.kind}>{jobKindLabel(job)}</span>
            {platform ? <PlatformChip platform={platform} /> : null}
            {channelLabel ? (
              <span className={cn(styles.channel, job.channel_kind === 'pool' && styles.channelPool)}>
                {channelLabel}
              </span>
            ) : null}
            <span className={styles.title}>{jobTitle(job)}</span>
          </div>
          {isActive && studioNow ? <StudioNow current={studioNow} /> : null}
          {isActive && engagementNow ? <EngagementNow item={engagementNow} /> : null}
          <div className={styles.meta}>
            {job.phase ? <span className={styles.phase}>{job.phase}</span> : null}
            {job.message ? <span>{job.message}</span> : null}
            {isActive && hosts?.current ? (
              <span className={styles.hostNow}>
                <HostIcon host={hosts.current} /> {llmHostLabel(hosts.current)}
              </span>
            ) : null}
            <span className={styles.jobId}>#{job.id.slice(-8)}</span>
          </div>
          {reason ? (
            <div className={styles.reason}>
              <button
                type="button"
                className={styles.reasonToggle}
                aria-expanded={showReason}
                aria-controls={reasonId}
                onClick={() => { setShowReason((v) => !v); }}
              >
                <AlertTriangle aria-hidden />
                {showReason ? 'Ocultar motivo' : 'Ver motivo'}
              </button>
              {showReason ? (
                <p id={reasonId} className={styles.reasonMessage}>
                  {reason}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className={styles.progressCell}>
          {isActive ? (
            <>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${String(progress)}%` }}
                  role="progressbar"
                  aria-valuenow={progress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
              <span className={styles.progressLabel}>{progress}%</span>
            </>
          ) : (
            <>
              {duration != null ? (
                <span className={styles.durationLabel}>{formatDurationHuman(duration)}</span>
              ) : null}
              <span className={styles.progressLabel}>{formatCompletedAt(job)}</span>
            </>
          )}
        </div>

        <div className={styles.actionsCell}>
          <CancelJobButton job={job} />
          {canRetry ? (
            <button
              type="button"
              className={styles.retryButton}
              onClick={onRetry}
              disabled={retry.isPending}
            >
              <RotateCcw aria-hidden />
              {retry.isPending ? 'Retomando…' : 'Continuar'}
            </button>
          ) : null}
          {hasDetail ? (
            <button
              type="button"
              className={styles.expandToggle}
              aria-expanded={expanded}
              aria-controls={detailId}
              onClick={() => { setExpanded((v) => !v); }}
            >
              <ChevronDown aria-hidden className={cn(styles.chevron, expanded && styles.chevronOpen)} />
              <span className={styles.srOnly}>{expanded ? 'Ocultar detalhes' : 'Ver detalhes'}</span>
            </button>
          ) : null}
          {firstVideo ? (
            <Link
              to="/projects/$projectId"
              params={{ projectId: firstVideo.id }}
              search={videoSearch(firstVideo)}
              className={styles.openLink}
            >
              Abrir
            </Link>
          ) : null}
        </div>
      </div>

      {expanded && hasDetail ? (
        <JobDetail
          id={detailId}
          job={job}
          videos={videos}
          engagement={engagement}
          duration={duration}
          warnings={warnings}
        />
      ) : null}
    </div>
  )
}

interface JobDetailProps {
  id: string
  job: Job
  videos: JobVideo[]
  engagement: EngagementItem[]
  duration: number | null
  warnings: string[]
}

function JobDetail({ id, job, videos, engagement, duration, warnings }: JobDetailProps) {
  const now = new Date()
  const hosts = job.llm_hosts ?? null
  const hostSummary = llmHostsSummary(hosts)
  return (
    <div id={id} className={styles.detail}>
      <dl className={styles.detailStats}>
        {duration != null ? (
          <div className={styles.detailStat}>
            <dt>Duração total</dt>
            <dd>{formatDurationHuman(duration)}</dd>
          </div>
        ) : null}
        {hostSummary ? (
          <div className={styles.detailStat}>
            <dt>LLM local</dt>
            <dd>
              {hostSummary}
              {hosts?.current ? (
                <span className={styles.hostNow}>
                  {' · agora '}
                  <HostIcon host={hosts.current} /> {llmHostLabel(hosts.current)}
                </span>
              ) : null}
            </dd>
          </div>
        ) : null}
      </dl>

      {warnings.length > 0 ? (
        <ul className={styles.warningList}>
          {warnings.map((w) => (
            <li key={w} className={styles.warning}>
              <AlertTriangle aria-hidden />
              <span>{w}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {engagement.length > 0 ? (
        <ul className={styles.videoList}>
          {engagement.map((item) => (
            <li key={item.key} className={styles.videoRow}>
              <span className={styles.videoName} title={item.text}>
                {item.text ? `“${item.text}”` : item.videoTitle || '—'}
                {item.author ? ` — ${item.author}` : ''}
              </span>
              <span className={styles.videoLang}>{ENGAGEMENT_KIND_LABEL[item.kind] ?? item.kind}</span>
              <span className={styles.videoDuration} title={item.detail || undefined}>
                {ENGAGEMENT_STATUS_LABEL[item.status] ?? item.status}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {videos.length > 0 ? (
        <ul className={styles.videoList}>
          {videos.map((v) => {
            const when = publishLabel(v.publishAt, now)
            return (
              <li key={`${v.id}-${v.language ?? ''}`} className={cn(styles.videoRow, when && styles.videoRowScheduled)}>
                {v.language ? (
                  <Link
                    to="/projects/$projectId"
                    params={{ projectId: v.id }}
                    search={videoSearch(v)}
                    className={styles.videoName}
                  >
                    {v.title || v.id}
                  </Link>
                ) : (
                  <span className={styles.videoName} title="O projeto não existe mais">
                    {v.title || v.id}
                  </span>
                )}
                <span className={styles.videoLang}>{v.language ?? '—'}</span>
                {when ? <span className={styles.videoWhen}>{when}</span> : null}
                <span className={styles.videoDuration} title={v.error || undefined}>
                  {v.studioStatus
                    ? studioStatusLabel(v.studioStatus, v.via)
                    : v.status === 'failed'
                      ? 'falhou'
                      : v.durationSeconds != null
                        ? formatDurationHuman(v.durationSeconds)
                        : '—'}
                </span>
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}

function HostIcon({ host }: { host: 'desktop' | 'server' }) {
  return host === 'desktop' ? (
    <Cpu aria-hidden className={styles.hostIcon} />
  ) : (
    <Server aria-hidden className={styles.hostIcon} />
  )
}

function statusIcon(status: JobStatus) {
  if (status === 'running' || status === 'pending')
    return <Loader aria-hidden className={styles.spin} />
  if (status === 'succeeded') return <CheckCircle2 aria-hidden />
  if (status === 'failed') return <XCircle aria-hidden />
  return <AlertTriangle aria-hidden />
}

function chipVariant(status: JobStatus): ChipVariant {
  if (status === 'succeeded') return 'success'
  if (status === 'failed' || status === 'cancelled') return 'danger'
  if (status === 'running') return 'info'
  return 'default'
}

/** Abre o projeto no canal onde ele está agora (vídeo do pool que já foi a um canal abre no canal). */
function videoSearch(v: JobVideo): (prev: Record<string, unknown>) => { lang: string; channel?: string } {
  const lang = v.language ?? 'pt-br'
  const channel = v.channel
  return channel ? () => ({ lang, channel }) : preservingChannel({ lang })
}

function jobTitle(job: Job): string {
  const d = job.detail
  if (!d || typeof d !== 'object') return '—'
  const title = typeof d['title'] === 'string' ? d['title'] : ''
  if (title) return title
  const theme = typeof d['theme'] === 'string' ? d['theme'] : ''
  if (theme) return theme
  const url = typeof d['url'] === 'string' ? d['url'] : ''
  if (url) return url
  return '—'
}

const STUDIO_STATUS_LABEL: Record<string, string> = {
  applied: 'capa aplicada',
  failed: 'falhou',
  skipped: 'sem arquivo',
}

// Job de backend anterior ao `via` (o Pages publica antes do pull do skab):
// sem rota conhecida, o rótulo não chuta uma.
const VIA_LABEL: Record<string, string> = {
  app: 'Aplicando agora · pelo celular',
  studio: 'Aplicando agora · pelo Studio',
}

function studioStatusLabel(status: string, via: string | null): string {
  const label = STUDIO_STATUS_LABEL[status] ?? status
  return via === 'app' ? `${label} · celular` : label
}

interface StudioCurrent {
  title: string
  channel: string
  thumbnailPath: string
  /** ``app`` quando a capa da vez sai pelo celular (canais sem verificação). */
  via: string | null
}

/**
 * Capa sendo aplicada agora pelo YouTube Studio (card #247). O backend grava
 * ``detail.current`` a cada vídeo e limpa no fim; o PNG vem pelo mesmo
 * ``/api/v1/media/thumbnails`` que o ThumbDock usa.
 */
function extractStudioCurrent(job: Job): StudioCurrent | null {
  if (job.type !== 'studio_thumbnail') return null
  const raw = job.detail && typeof job.detail === 'object' ? job.detail['current'] : null
  if (!raw || typeof raw !== 'object') return null
  const obj = raw as Record<string, unknown>
  const thumbnailPath = typeof obj['thumbnail_path'] === 'string' ? obj['thumbnail_path'] : ''
  if (!thumbnailPath) return null
  return {
    title: typeof obj['title'] === 'string' ? obj['title'] : '',
    channel: typeof obj['channel_slug'] === 'string' ? obj['channel_slug'] : '',
    thumbnailPath,
    via: typeof obj['via'] === 'string' ? obj['via'] : null,
  }
}

function StudioNow({ current }: { current: StudioCurrent }) {
  const src = thumbnailUrl(current.thumbnailPath)
  return (
    <div className={styles.studioNow}>
      {src ? <img src={src} alt="" className={styles.studioThumb} /> : null}
      <div className={styles.studioNowText}>
        <span className={styles.studioNowLabel}>
          {VIA_LABEL[current.via ?? ''] ?? 'Aplicando agora'}
        </span>
        <span className={styles.studioNowTitle}>{current.title}</span>
        {current.channel ? <span className={styles.studioNowChannel}>{current.channel}</span> : null}
      </div>
    </div>
  )
}

const ENGAGEMENT_KIND_LABEL: Record<string, string> = {
  heart: 'coração',
  pin: 'fixado',
  post: 'post',
}

const ENGAGEMENT_STATUS_LABEL: Record<string, string> = {
  done: 'feito',
  failed: 'falhou',
  rejected: 'reprovado',
}

const ENGAGEMENT_NOW_LABEL: Record<string, string> = {
  heart: 'Dando coração agora',
  pin: 'Fixando agora',
  post: 'Programando agora',
}

interface EngagementItem {
  key: string
  kind: string
  channel: string
  text: string
  author: string
  videoTitle: string
  status: string
  /** Erro da ação, ou o motivo da reprovação na segurança. */
  detail: string
}

function str(obj: Record<string, unknown>, key: string): string {
  const v = obj[key]
  return typeof v === 'string' ? v : ''
}

function toEngagementItem(raw: unknown, fallbackStatus: string, index: number): EngagementItem | null {
  if (!raw || typeof raw !== 'object') return null
  const obj = raw as Record<string, unknown>
  const rawId = obj['id'] ?? obj['comment_id']
  const id = typeof rawId === 'string' || typeof rawId === 'number' ? String(rawId) : String(index)
  return {
    key: `${fallbackStatus}-${id}`,
    kind: str(obj, 'kind') || 'heart',
    channel: str(obj, 'channel_slug'),
    text: str(obj, 'text'),
    author: str(obj, 'author'),
    videoTitle: str(obj, 'video_title'),
    status: str(obj, 'status') || fallbackStatus,
    detail: str(obj, 'error') || str(obj, 'reason'),
  }
}

/**
 * Ações de engajamento pelo Studio (card #255): o que foi feito em
 * ``detail.actions`` e o que a segurança barrou em ``detail.rejected``.
 */
function extractEngagementItems(job: Job): EngagementItem[] {
  if (job.type !== 'studio_engagement' || !job.detail || typeof job.detail !== 'object') return []
  const actions = job.detail['actions']
  const rejected = job.detail['rejected']
  return [
    ...(Array.isArray(actions) ? actions.map((a, i) => toEngagementItem(a, 'done', i)) : []),
    ...(Array.isArray(rejected) ? rejected.map((r, i) => toEngagementItem(r, 'rejected', i)) : []),
  ].filter((v): v is EngagementItem => v !== null)
}

function extractEngagementCurrent(job: Job): EngagementItem | null {
  if (job.type !== 'studio_engagement' || !job.detail || typeof job.detail !== 'object') return null
  return toEngagementItem(job.detail['current'], 'running', 0)
}

function EngagementNow({ item }: { item: EngagementItem }) {
  return (
    <div className={styles.studioNow}>
      <div className={styles.studioNowText}>
        <span className={styles.studioNowLabel}>{ENGAGEMENT_NOW_LABEL[item.kind] ?? 'Agora'}</span>
        <span className={styles.studioNowTitle}>
          {item.text ? `“${item.text}”` : item.videoTitle}
          {item.author ? ` — ${item.author}` : ''}
        </span>
        {item.channel ? <span className={styles.studioNowChannel}>{item.channel}</span> : null}
      </div>
    </div>
  )
}

/**
 * Avisos que o job deixou em ``detail.warnings`` — o que não é falha mas o
 * operador precisa saber (ex.: canais sem verificação no YouTube, card #247).
 */
function extractWarnings(job: Job): string[] {
  const raw = job.detail && typeof job.detail === 'object' ? job.detail['warnings'] : null
  if (!Array.isArray(raw)) return []
  return raw.filter((w): w is string => typeof w === 'string' && w.length > 0)
}

function formatCompletedAt(job: Job): string {
  // Relative time is nicer for "recent", but formatter lives in a shared
  // lib only if we need it for other features — keep it inline for now.
  const iso = job.finished_at ?? job.updated_at
  if (!iso) return ''
  const d = new Date(iso)
  const diffMs = Date.now() - d.getTime()
  const diffMin = Math.round(diffMs / 60_000)
  if (diffMin < 1) return 'agora'
  if (diffMin < 60) return `${String(diffMin)}min atrás`
  const diffH = Math.round(diffMin / 60)
  if (diffH < 24) return `${String(diffH)}h atrás`
  const diffD = Math.round(diffH / 24)
  return `${String(diffD)}d atrás`
}
